// The Python course island: the Python logo hovering over the platform.
import React, { useRef, useState } from 'react';
import { Float } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { Fitted, HaloRing, PLATFORM_TOP, ShadowBlob, useCloned, usePointerCursor } from './helpers';

/** Flat plate — a full spin would make it vanish edge-on, so it sways instead. */
const Logo = ({ top, reduced }) => {
  const object = useCloned('/Python.glb');
  const group = useRef();
  const light = useRef();
  const burst = useRef(0);
  const [hovered, setHovered] = useState(false);
  const [reducedTurn, setReducedTurn] = useState(0);
  usePointerCursor(hovered);

  useFrame(({ clock }, delta) => {
    if (!group.current) return;

    if (reduced) {
      group.current.rotation.set(0, reducedTurn * (Math.PI / 2), 0);
      group.current.scale.setScalar(hovered ? 1.06 : 1);
      if (light.current) light.current.intensity = 2.2;
      return;
    }

    let spin = 0;
    let pulse = 0;
    let wobble = 0;
    if (burst.current > 0) {
      const progress = 1 - burst.current / 1.05;
      const eased = 1 - Math.pow(1 - progress, 3);
      pulse = Math.sin(progress * Math.PI);
      spin = eased * Math.PI * 2;
      wobble = Math.sin(progress * Math.PI * 2) * pulse * 0.12;
      burst.current = Math.max(0, burst.current - delta);
    }

    group.current.rotation.y = Math.sin(clock.getElapsedTime() * 0.5) * 0.42 + spin;
    group.current.rotation.z = wobble;
    const targetScale = 1 + (hovered ? 0.08 : 0) + pulse * 0.16;
    group.current.scale.setScalar(THREE.MathUtils.damp(group.current.scale.x, targetScale, 12, delta));
    if (light.current) light.current.intensity = 2.2 + pulse * 3.2;
  });

  const activate = (event) => {
    event.stopPropagation();
    if (reduced) {
      setReducedTurn((turn) => (turn + 1) % 4);
    } else if (burst.current <= 0) {
      burst.current = 1.05;
    }
  };

  return (
    <group
      ref={group}
      position={[0, top + 1.5, 0]}
      onPointerOver={(event) => { event.stopPropagation(); setHovered(true); }}
      onPointerOut={() => setHovered(false)}
      onClick={activate}
    >
      <Fitted object={object} size={2} fit="max" align="center" />
      {/* Lifts the logo off the background so it reads as lit from within. */}
      <pointLight ref={light} color="#FFD97A" intensity={2.2} distance={5} decay={2} />
    </group>
  );
};

const PythonIsland = ({ accent, accentInk, reduced }) => {
  const platform = useCloned('/GodotBotPlatform.glb');
  const [top, setTop] = useState(PLATFORM_TOP);

  const logo = <Logo top={top} reduced={reduced} />;

  return (
    <group>
      <Fitted object={platform} size={5} fit="max" onFit={({ height }) => setTop(height)} />
      <ShadowBlob radius={3.1} opacity={0.55} position={[0, top + 0.012, 0]} />
      <HaloRing accent={accent} accentInk={accentInk} radius={2.5} position={[0, top - 0.4, 0]} />

      {reduced ? (
        logo
      ) : (
        <Float speed={1.3} rotationIntensity={0.15} floatIntensity={0.55} floatingRange={[0, 0.2]}>
          {logo}
        </Float>
      )}
    </group>
  );
};

export default PythonIsland;
